import { useEffect, useState } from "react";
import { ShoppingCart, Menu, X, Phone, User } from "lucide-react";
import { LogoFull } from "./Logo";
import { useCart } from "../context/CartContext";
import { BRAND } from "../constants/brand";

const LINKS = [
  { id: "fahrt", label: "Transport buchen", testid: "nav-link-fahrt-buchen" },
  { id: "shop", label: "Shop", testid: "nav-link-fahrzeuge" },
  { id: "service", label: "Service-Werkstatt", testid: "nav-link-service" },
  { id: "kontakt", label: "Kontakt", testid: "nav-link-kontakt" },
];

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -72 });
  } else {
    el.scrollIntoView({ behavior: "smooth" });
  }
};

export const Navbar = () => {
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-500 ${
        scrolled ? "bg-slate-950/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-[72px] flex items-center justify-between">
        <button onClick={() => scrollToId("hero")} className="shrink-0">
          <LogoFull />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => scrollToId(l.id)}
              className="text-sm text-slate-300 hover:text-cyan-300 transition-colors duration-300 tracking-wide"
            >
              {l.label}
            </button>
          ))}
          <a
            data-testid="nav-link-verfolgung"
            href="/verfolgung"
            className="text-sm text-slate-300 hover:text-cyan-300 transition-colors duration-300 tracking-wide"
          >
            Verfolgung
          </a>
          <a
            data-testid="nav-link-firmenkunden"
            href="/geschaeftskunden"
            className="text-sm text-slate-300 hover:text-cyan-300 transition-colors duration-300 tracking-wide"
          >
            Firmenkunden
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${BRAND.phone}`}
            className="hidden md:flex items-center gap-2 text-xs font-mono-tech text-slate-400 hover:text-cyan-300 transition-colors"
          >
            <Phone size={14} /> {BRAND.phone}
          </a>
          <a
            data-testid="nav-konto-link"
            href="/konto"
            className="hidden md:flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-sm text-slate-300 hover:text-cyan-300 hover:border-cyan-400/30 transition-colors duration-300"
          >
            <User size={15} /> Konto
          </a>
          <button
            data-testid="cart-trigger-button"
            onClick={() => setOpen(true)}
            className="relative flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-4 py-2 text-sm text-cyan-300 hover:bg-cyan-400/20 transition-colors duration-300"
          >
            <ShoppingCart size={16} />
            <span className="hidden sm:inline">Warenkorb</span>
            {count > 0 && (
              <span className="absolute -top-2 -right-2 min-w-[20px] h-5 rounded-full bg-gradient-to-r from-[#1E6FD9] to-[#29B6F6] text-[11px] font-bold text-white flex items-center justify-center px-1.5">
                {count}
              </span>
            )}
          </button>
          <button
            className="lg:hidden text-slate-200 p-2"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menü"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-slate-950/95 backdrop-blur-xl border-b border-white/10 px-6 py-5 flex flex-col gap-4">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`${l.testid}-mobile`}
              onClick={() => {
                setMobileOpen(false);
                scrollToId(l.id);
              }}
              className="text-left text-base text-slate-200 py-1"
            >
              {l.label}
            </button>
          ))}
          <a
            data-testid="nav-link-verfolgung-mobile"
            href="/verfolgung"
            className="text-left text-base text-slate-200 py-1"
          >
            Verfolgung
          </a>
          <a
            data-testid="nav-link-firmenkunden-mobile"
            href="/geschaeftskunden"
            className="text-left text-base text-slate-200 py-1"
          >
            Firmenkunden
          </a>
        </div>
      )}
    </header>
  );
};
